import { db, DBHistoryEntry } from '../../db/schema';
import { generateId } from '../utils';
import type { HttpRequest, HttpResponse } from '../../stores';

export interface SaveHistoryOptions {
  request: HttpRequest;
  response?: HttpResponse;
  error?: { message: string; code?: string };
  workspaceId: string;
  environmentId?: string;
  requestId?: string;
}

// Event emitter for history changes
type HistoryChangeListener = () => void;
const historyChangeListeners: Set<HistoryChangeListener> = new Set();

export function onHistoryChange(listener: HistoryChangeListener): () => void {
  historyChangeListeners.add(listener);
  return () => historyChangeListeners.delete(listener);
}

function notifyHistoryChange(): void {
  historyChangeListeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      console.error('Error in history change listener:', error);
    }
  });
}

/**
 * Save an executed request to history (IndexedDB)
 */
export async function saveToHistory(options: SaveHistoryOptions): Promise<DBHistoryEntry> {
  const { request, response, error, workspaceId, environmentId, requestId } = options;

  const historyEntry: DBHistoryEntry = {
    id: generateId(),
    requestId,
    workspaceId,
    request: {
      name: request.name,
      method: request.method,
      url: request.url,
      headers: request.headers,
      params: request.params,
      body: request.body,
      auth: request.auth,
    },
    response: response ? {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      body: response.body,
      size: response.size,
      time: response.time,
    } : undefined,
    error,
    executedAt: Date.now(),
    environmentId,
  };

  // Save to IndexedDB
  await db.history.add(historyEntry);

  // Notify listeners
  notifyHistoryChange();

  return historyEntry;
}

/**
 * Get recent history entries for a workspace
 */
export async function getRecentHistory(workspaceId: string, limit: number = 50): Promise<DBHistoryEntry[]> {
  return db.getRecentHistory(workspaceId, limit);
}

/**
 * Clear all history for a workspace
 */
export async function clearHistory(workspaceId: string): Promise<void> {
  await db.clearHistory(workspaceId);
  notifyHistoryChange();
}

/**
 * Delete a specific history entry
 */
export async function deleteHistoryEntry(id: string): Promise<void> {
  await db.history.delete(id);
  notifyHistoryChange();
}
